import { ApplicationStatus } from 'modules/teams/domain/enums/application-status';
import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { invalidatePlayersList } from 'modules/players/store/actions';
import PlayerForm, { SubmitPlayerForm } from 'modules/players/ui/components/player-form';
import { addPlayerApplication } from 'modules/teams/store/championship-teams';
import { TeamPlayer } from 'modules/teams/domain/interfaces/team-player';
import { findPlayerInSquad } from 'modules/teams/domain/utils/squad-duplicate';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppDispatch } from 'store/store';
import { upsertTournamentPlayerApplication } from 'modules/teams/store/players/actions';

interface CreatePlayerProps {
  tournamentId: number;
  teamId: number;
  /** Весь состав команды — и заведённые вручную игроки, и вступившие участники. */
  players: TeamPlayer[];
}

const CreatePlayer = (props: CreatePlayerProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { tournamentId, teamId, players } = props;
  const [isLoading, setIsLoading] = useState(false);
  const playerId = Number(useLocation().state?.fromPlayer);

  const handleDisableForm = useCallback(() => {
    navigate(Pages.TEAMS.builders.edit(teamId, 'players'), {
      state: { fromPlayer: playerId },
    });
  }, [navigate, playerId, teamId]);

  const handleSubmit = useCallback<SubmitPlayerForm>(
    async ({ values, formikHelpers }) => {
      const photo = values.photo && values.photo[0] instanceof File ? (values.photo as File[]) : undefined;
      const height = values.height && !isNaN(Number(values.height)) ? Number(values.height) : undefined;
      const weight = values.weight && !isNaN(Number(values.weight)) ? Number(values.weight) : undefined;

      // Тот же игрок уже в составе — на бэкенд не ходим вовсе: он ответит
      // «User is already in this team», но запрос и ожидание пользователю ни к чему.
      const duplicate = findPlayerInSquad(players, {
        userId: values.id,
        name: values.name,
        surname: values.surname,
        middleName: values.middleName,
        birthDate: values.birthDate,
      });

      if (duplicate) {
        toast.error('Игрок уже в этой команде');
        formikHelpers.setSubmitting(false);
        return;
      }

      setIsLoading(true);

      let data;
      try {
        // Тост об отказе вешаем только на саму операцию: если упадёт постобработка
        // ниже (навигация, синхронизация кэша), пользователь не должен видеть
        // «Не удалось выполнить операцию» на успешно созданном игроке.
        data = await dispatch(
          upsertTournamentPlayerApplication({
            userId: values.id,
            teamId,
            tournamentId,
            name: values.name,
            surname: values.surname,
            middleName: values.middleName,
            birthDate: values.birthDate,
            height,
            weight,
            photo,
          }),
        ).unwrap();
      } catch (error) {
        if (error === 'User is already in this team') {
          toast.error('Игрок уже в этой команде');
        } else if (error === 'Tournament user created') {
          toast.error('Такой игрок уже существует');
        } else {
          toast.error('Не удалось выполнить операцию');
        }
        return;
      } finally {
        formikHelpers.setSubmitting(false);
        setIsLoading(false);
      }

      // Тот же текст, что на странице создания игрока (`players/ui/pages/player-create-page.tsx`),
      // — операция одна и та же, отличается только точка входа.
      toast.success('Игрок успешно создан');

      handleDisableForm();
      StepperMainTool.setByPath('stepper:main:createPlayer', true);

      // Игрок создан — кэш списка /players устарел: сбрасываем тег перечня,
      // и страница списка перечитает его при следующем показе.
      dispatch(invalidatePlayersList());
      dispatch(
        addPlayerApplication({
          teamId,
          application: {
            teamId,
            player: {
              id: data.id,
              firstName: data.name,
              lastName: data.surname,
              middleName: data.middleName,
              birthDate: data.birthDate,
              height: data.height,
              weight: data.weight,
              logoUrl: data.tournamentAvatars?.[0]?.image?.externalUrl,
            },
            status: ApplicationStatus.PENDING,
            id: data.id,
            // Заявка создаётся прямо сейчас. Раньше здесь стояло `data.createdAt`,
            // но запрос игрока это поле не выбирал, и в заявку всегда попадал undefined.
            createdAt: new Date().toISOString(),
          },
        }),
      );
    },
    [dispatch, handleDisableForm, players, teamId, tournamentId],
  );

  return (
    <div className="pt-6">
      <PlayerForm onSubmit={handleSubmit} isLoading={isLoading} />
    </div>
  );
};

export default CreatePlayer;
